/**
 * Ein Anmelde-Token aus `pawbot://login?t=…` wird genau EINMAL getauscht.
 * (Ticket 2314; Riegel seit 16.09.2026, hier seit 23.09.2026)
 *
 * WARUM ES DEN RIEGEL GIBT: Der Tausch endet in `redirect("/")`, die WebView
 * lädt komplett neu, und `getLaunchUrl()` liefert danach dieselbe Adresse
 * noch einmal. Ein zweiter Tausch trifft ein verbrauchtes Token und landet
 * auf `/?login_error=native`.
 *
 * WARUM localStorage UND NICHT MEHR sessionStorage (23.09.2026): Der Riegel
 * lag bis heute in sessionStorage, mit der Annahme „überlebt das Neuladen,
 * wird beim echten Kaltstart geleert — genau die Lebensdauer, die gebraucht
 * wird". Die Annahme hält nicht, am Protokoll belegt:
 *
 *   - Capacitor liefert mit `getLaunchUrl()` nicht die Start-Adresse, sondern
 *     `ApplicationDelegateProxy.lastURL` — die ZULETZT geöffnete Adresse. Sie
 *     wird nie geleert und bleibt, solange der App-Prozess lebt.
 *   - Am 18.09. hat die App-WebView (UA „Macintosh … (KHTML, like Gecko)" —
 *     Mac oder iPad im Desktop-Modus, am Log nicht zu unterscheiden) das am
 *     16.09. um 16:18 längst verbrauchte Token 736b68c7 sechsmal erneut
 *     getauscht (07:20, 13:17, 13:47, 14:46, 16:16, 16:46). Vor jedem Versuch
 *     meldete /api/auth/me eine gültige Sitzung; jeder Versuch lud die App ein
 *     weiteres Mal neu. Bei diesen Ladungen waren BEIDE sessionStorage-Riegel
 *     leer (dieser und `pawbot:launchHandled`), `lastURL` aber noch da.
 *   - 23.09.2026 (Ticket 2314) — WARUM sessionStorage verloren ging, ist
 *     OFFEN. Nicht jede Neuladung verlor ihn: von den zwölf im nginx-Log
 *     sichtbaren Neuladungen zwischen 11:51 und 17:45 lösten nur fünf einen
 *     Tausch aus, bei den übrigen sieben hielt sessionStorage. Die Ladung um
 *     07:20 war das Versions-Neuladen der App selbst (HEAD /?ping davor),
 *     nach gut anderthalb Tagen ohne jede Anfrage. Ende des
 *     WebContent-Prozesses, neue Szene oder Netzprozess sind Kandidaten,
 *     keiner ist belegt.
 *
 * localStorage liegt auf der Platte und übersteht jede dieser Varianten.
 * Weil der Riegel am TOKEN hängt (eine Einmal-UUID), schadet es nicht, dass
 * er bleibt: ein neues Token — etwa beim Wechsel des Kontos — wird ganz
 * normal getauscht.
 *
 * Bewusst NICHT „überspringen, wenn /api/auth/me schon gültig ist": das
 * sperrte genau den Nutzerwechsel, der über einen Kaltstart per Deep-Link
 * hereinkommt, und hinge den Tausch an eine Netzantwort.
 *
 * Fällt der Speicher aus, wird getauscht. Ein Tausch zu viel ist ein
 * Fehlerbild (und seit 23.09. fängt der Server ihn bei gültiger Sitzung
 * still ab); ein Tausch zu wenig wäre eine Anmeldung, die nicht stattfindet.
 */
const TAUSCH_RIEGEL = "pawbot:loginTokenGetauscht";

function istSchonGetauscht(token: string): boolean {
  try {
    return localStorage.getItem(TAUSCH_RIEGEL) === token;
  } catch {
    return false;
  }
}

function merkeGetauscht(token: string): void {
  try {
    localStorage.setItem(TAUSCH_RIEGEL, token);
  } catch {
    /* ohne Speicher bleibt es beim Tauschen */
  }
}

type TauschEntscheid =
  | { tauschen: true; ziel: string }
  | { tauschen: false; grund: "kein-token" | "schon-getauscht" };

/**
 * Entscheidet für einen `pawbot://login?t=…`-Link, ob getauscht wird.
 * Bei `tauschen: true` ist der Riegel bereits gesetzt — der Aufrufer MUSS
 * dann nach `ziel` navigieren.
 */
export function tauschEinmal(rawUrl: string): TauschEntscheid {
  let tok: string | null = null;
  try {
    tok = new URL(rawUrl).searchParams.get("t");
  } catch {
    /* kaputter Deep-Link — wie „kein Token" */
  }
  if (!tok) return { tauschen: false, grund: "kein-token" };
  if (istSchonGetauscht(tok)) return { tauschen: false, grund: "schon-getauscht" };
  merkeGetauscht(tok);
  return {
    tauschen: true,
    ziel: "/api/auth/native-set?t=" + encodeURIComponent(tok),
  };
}
